// src/Components/Recommendations.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Rating,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ImageList,
  ImageListItem,
  Collapse,
  Badge,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Place as PlaceIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon,
  LocalHospital as HealthcareIcon,
  School as SchoolIcon,
  Park as ParkIcon,
  ShoppingBag as ShoppingIcon,
  Business as BusinessIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  Photo as PhotoIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Directions as DirectionsIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { sanitizeImageUrl } from '../utils/urlSanitizer';
import PrivacyControl from './PrivacyControl';
import LocationPicker, { buildMapsUrl, buildDirectionsUrl } from './LocationPicker';
import TagMembers from './TagMembers';
import { notifyTaggedMembers } from '../services/notificationService';
import logger from '../utils/logger';

// US States list
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const Recommendations = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [visitedDialogOpen, setVisitedDialogOpen] = useState(false);
  const [visitedData, setVisitedData] = useState({ date: new Date().toISOString().split('T')[0], rating: 3, notes: '' });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Filter and sort states
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRating, setFilterRating] = useState(0);
  const [filterPrice, setFilterPrice] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Count active filters for badge
  const activeFilterCount = [
    filterCategory !== 'all',
    filterState !== 'all',
    filterRating > 0,
    filterPrice !== 'all'
  ].filter(Boolean).length;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'restaurant',
    location: null,
    rating: 3,
    priceRange: '$$',
    tags: '',
    photos: [],
    contactPhone: '',
    contactWebsite: '',
    contactInstagram: '',
    contactFacebook: '',
    contactLinkedIn: '',
    contactTwitter: '',
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });

  const db = getFirestore();
  const storage = getStorage();

  const categories = [
    { value: 'restaurant', label: 'Restaurants', icon: <RestaurantIcon />, color: '#f57c00' },
    { value: 'activity', label: 'Activities/Entertainment', icon: <ActivityIcon />, color: '#7b1fa2' },
    { value: 'healthcare', label: 'Healthcare/Services', icon: <HealthcareIcon />, color: '#d32f2f' },
    { value: 'school', label: 'Schools/Education', icon: <SchoolIcon />, color: '#1976d2' },
    { value: 'park', label: 'Parks/Outdoors', icon: <ParkIcon />, color: '#388e3c' },
    { value: 'shopping', label: 'Shopping', icon: <ShoppingIcon />, color: '#e91e63' },
    { value: 'service', label: 'Professional Services', icon: <BusinessIcon />, color: '#6d4c41' }
  ];

  const priceRanges = ['$', '$$', '$$$', '$$$$'];

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'recommendations'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const recommendationsData = [];
        querySnapshot.forEach((doc) => {
          recommendationsData.push({ id: doc.id, ...doc.data() });
        });
        setRecommendations(recommendationsData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching recommendations:', error);
        setError('Failed to load recommendations');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  // Helper function to check if user can view a recommendation based on privacy settings
  const canViewRecommendation = (recommendation) => {
    // Recommendation author can always see their own recommendations
    if (recommendation.createdBy === currentUser?.uid) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!recommendation.privacy || recommendation.privacy.visibility === 'public') {
      return true;
    }

    // For private recommendations, check if user is in the allowed list
    if (recommendation.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (recommendation.privacy.memberIds && recommendation.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (recommendation.privacy.groupTypes && recommendation.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = recommendation.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private recommendation
      return false;
    }

    return true;
  };

  // Apply filters and sorting
  useEffect(() => {
    // First filter by privacy
    let filtered = recommendations.filter(recommendation => canViewRecommendation(recommendation));

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(rec => rec.category === filterCategory);
    }

    // Filter by rating
    if (filterRating > 0) {
      filtered = filtered.filter(rec => rec.rating >= filterRating);
    }

    // Filter by price
    if (filterPrice !== 'all') {
      filtered = filtered.filter(rec => rec.priceRange === filterPrice);
    }

    // Filter by state
    if (filterState !== 'all') {
      filtered = filtered.filter(rec => rec.state === filterState);
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'likes':
        filtered.sort((a, b) => getLikeCount(b.likes) - getLikeCount(a.likes));
        break;
      default:
        break;
    }

    setFilteredRecommendations(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations, filterCategory, filterRating, filterPrice, filterState, sortBy]);

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length + formData.photos.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }

    setPhotoUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const storageRef = ref(storage, `families/${familyBoardId}/recommendations/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const photoUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...photoUrls] }));
      setPhotoUploading(false);
    } catch (error) {
      logger.error('Error uploading photos:', error);
      setError('Failed to upload photos');
      setPhotoUploading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = formData.photos.filter((_, i) => i !== index);
    setFormData({ ...formData, photos: newPhotos });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Recommendation name is required');
      return;
    }

    if (!formData.location) {
      setError('Location is required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private recommendations');
      return;
    }

    try {
      const recommendationData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        location: formData.location,
        address: formData.location?.address || formData.location?.name || '',
        rating: formData.rating,
        priceRange: formData.priceRange,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        photos: formData.photos,
        contactPhone: formData.contactPhone || '',
        contactWebsite: formData.contactWebsite || '',
        contactInstagram: formData.contactInstagram || '',
        contactFacebook: formData.contactFacebook || '',
        contactLinkedIn: formData.contactLinkedIn || '',
        contactTwitter: formData.contactTwitter || '',
        likes: {},
        comments: [],
        visited: {},
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      };

      let recommendationId = editingRecommendation?.id;
      if (editingRecommendation) {
        await updateDoc(doc(db, 'families', familyBoardId, 'recommendations', editingRecommendation.id), {
          ...recommendationData,
          likes: editingRecommendation.likes,
          comments: editingRecommendation.comments,
          visited: editingRecommendation.visited,
          updatedAt: serverTimestamp()
        });
        setSuccess('Recommendation updated');
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'recommendations'), recommendationData);
        recommendationId = docRef.id;
        setSuccess('Recommendation added');

        // Send notifications to tagged members (only for new recommendations)
        if (formData.taggedMembers && formData.taggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
            type: 'recommendation',
            id: recommendationId,
            title: formData.name,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/recommendations'
          });
        }
      }

      setDialogOpen(false);
      setEditingRecommendation(null);
      resetForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving recommendation:', error);
      setError('Failed to save recommendation');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'restaurant',
      location: null,
      rating: 3,
      priceRange: '$$',
      tags: '',
      photos: [],
      contactPhone: '',
      contactWebsite: '',
      contactInstagram: '',
      contactFacebook: '',
      contactLinkedIn: '',
      contactTwitter: '',
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: []
    });
  };

  const handleDelete = async (id, photos) => {
    if (!window.confirm('Are you sure you want to delete this recommendation?')) return;

    try {
      // Delete photos from storage
      if (photos && photos.length > 0) {
        const deletePromises = photos.map(photoUrl => {
          try {
            const photoRef = ref(storage, photoUrl);
            return deleteObject(photoRef);
          } catch (err) {
            logger.error('Error deleting photo:', err);
            return Promise.resolve();
          }
        });
        await Promise.all(deletePromises);
      }

      await deleteDoc(doc(db, 'families', familyBoardId, 'recommendations', id));
      setSuccess('Recommendation deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting recommendation:', error);
      setError('Failed to delete recommendation');
    }
  };

  const handleLike = async (recommendationId) => {
    try {
      const recommendation = recommendations.find(r => r.id === recommendationId);
      const likes = recommendation.likes || {};

      if (likes[currentUser.uid]) {
        delete likes[currentUser.uid];
      } else {
        likes[currentUser.uid] = {
          name: currentUser.displayName || 'Anonymous',
          timestamp: new Date().toISOString()
        };
      }

      await updateDoc(doc(db, 'families', familyBoardId, 'recommendations', recommendationId), { likes });
    } catch (error) {
      logger.error('Error updating like:', error);
      setError('Failed to update like');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const recommendation = recommendations.find(r => r.id === selectedRecommendation.id);
      const comments = recommendation.comments || [];

      comments.push({
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        comment: newComment,
        timestamp: new Date().toISOString()
      });

      await updateDoc(doc(db, 'families', familyBoardId, 'recommendations', selectedRecommendation.id), { comments });
      setNewComment('');
      setSuccess('Comment added');
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error adding comment:', error);
      setError('Failed to add comment');
    }
  };

  const handleMarkVisited = async () => {
    try {
      const recommendation = recommendations.find(r => r.id === selectedRecommendation.id);
      const visited = recommendation.visited || {};

      visited[currentUser.uid] = {
        name: currentUser.displayName || 'Anonymous',
        date: visitedData.date,
        rating: visitedData.rating,
        notes: visitedData.notes,
        timestamp: new Date().toISOString()
      };

      await updateDoc(doc(db, 'families', familyBoardId, 'recommendations', selectedRecommendation.id), { visited });
      setVisitedDialogOpen(false);
      setVisitedData({ date: new Date().toISOString().split('T')[0], rating: 3, notes: '' });
      setSuccess('Marked as visited');
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      logger.error('Error marking as visited:', error);
      setError('Failed to mark as visited');
    }
  };

  const openEditDialog = (recommendation) => {
    setEditingRecommendation(recommendation);
    setFormData({
      name: recommendation.name,
      description: recommendation.description || '',
      category: recommendation.category,
      location: (() => {
        const loc = recommendation.location;
        if (loc?.name || loc?.address) return loc; // new format
        // build from legacy separate fields
        const addrParts = [recommendation.address,
          recommendation.city, recommendation.state,
          recommendation.country !== 'United States' ? recommendation.country : null
        ].filter(Boolean);
        const addrStr = addrParts.join(', ');
        return addrStr ? { name: recommendation.name, address: addrStr, lat: loc?.lat || null, lng: loc?.lng || null } : null;
      })(),
      rating: recommendation.rating || 3,
      priceRange: recommendation.priceRange || '$$',
      tags: recommendation.tags ? recommendation.tags.join(', ') : '',
      photos: recommendation.photos || [],
      contactPhone: recommendation.contactPhone || '',
      contactWebsite: recommendation.contactWebsite || '',
      contactInstagram: recommendation.contactInstagram || '',
      contactFacebook: recommendation.contactFacebook || '',
      contactLinkedIn: recommendation.contactLinkedIn || '',
      contactTwitter: recommendation.contactTwitter || '',
      privacy: recommendation.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: recommendation.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingRecommendation(null);
    resetForm();
    setDialogOpen(true);
  };

  const openCommentDialog = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setCommentDialogOpen(true);
  };

  const openVisitedDialog = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setVisitedDialogOpen(true);
  };

  const openDetailDialog = (recommendation) => {
    setSelectedRecommendation(recommendation);
    setDetailDialogOpen(true);
  };

  const getCategoryDetails = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  const getLikeCount = (likes) => {
    return likes ? Object.keys(likes).length : 0;
  };

  const hasUserLiked = (likes) => {
    return likes && likes[currentUser.uid];
  };

  const getVisitedCount = (visited) => {
    return visited ? Object.keys(visited).length : 0;
  };

  const hasUserVisited = (visited) => {
    return visited && visited[currentUser.uid];
  };

  const getCommentCount = (comments) => {
    return comments ? comments.length : 0;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Recommendations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share and discover great places, activities, and services
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
        >
          Add Recommendation
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filter and Sort Controls */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        {/* Mobile: Collapsible filter header */}
        {isMobile && (
          <Box
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              mb: filtersExpanded ? 2 : 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge badgeContent={activeFilterCount} color="primary">
                <FilterListIcon />
              </Badge>
              <Typography variant="subtitle1" fontWeight={500}>
                Filters & Sort
              </Typography>
            </Box>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}

        {/* Filter controls - always visible on desktop, collapsible on mobile */}
        <Collapse in={!isMobile || filtersExpanded} timeout="auto">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={filterState}
                  label="State"
                  onChange={(e) => setFilterState(e.target.value)}
                >
                  <MenuItem value="all">All States</MenuItem>
                  {US_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Min Rating</InputLabel>
                <Select
                  value={filterRating}
                  label="Min Rating"
                  onChange={(e) => setFilterRating(e.target.value)}
                >
                  <MenuItem value={0}>All Ratings</MenuItem>
                  <MenuItem value={3}>3+ Stars</MenuItem>
                  <MenuItem value={4}>4+ Stars</MenuItem>
                  <MenuItem value={5}>5 Stars</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Price Range</InputLabel>
                <Select
                  value={filterPrice}
                  label="Price Range"
                  onChange={(e) => setFilterPrice(e.target.value)}
                >
                  <MenuItem value="all">All Prices</MenuItem>
                  {priceRanges.map((price) => (
                    <MenuItem key={price} value={price}>{price}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="rating">Highest Rated</MenuItem>
                  <MenuItem value="likes">Most Liked</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Clear filters button - show when any filter is active */}
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  setFilterCategory('all');
                  setFilterState('all');
                  setFilterRating(0);
                  setFilterPrice('all');
                }}
              >
                Clear Filters ({activeFilterCount})
              </Button>
            </Box>
          )}
        </Collapse>
      </Paper>

      {filteredRecommendations.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <PlaceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {filterCategory !== 'all' || filterRating > 0 || filterPrice !== 'all' || filterState !== 'all'
              ? 'No recommendations match your filters'
              : 'No recommendations shared yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {filterCategory !== 'all' || filterRating > 0 || filterPrice !== 'all' || filterState !== 'all'
              ? 'Try adjusting your filters'
              : 'Start sharing your favorite places and activities with your family!'}
          </Typography>
          {filterCategory === 'all' && filterRating === 0 && filterPrice === 'all' && filterState === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Add First Recommendation
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredRecommendations.map((recommendation) => {
            const categoryDetails = getCategoryDetails(recommendation.category);
            const likeCount = getLikeCount(recommendation.likes);
            const userLiked = hasUserLiked(recommendation.likes);
            const visitedCount = getVisitedCount(recommendation.visited);
            const userVisited = hasUserVisited(recommendation.visited);
            const commentCount = getCommentCount(recommendation.comments);

            return (
              <Grid item xs={12} md={6} lg={4} key={recommendation.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-4px)', cursor: 'pointer' },
                    transition: 'all 0.3s',
                    border: '2px solid',
                    borderColor: categoryDetails.color
                  }}
                  onClick={() => openDetailDialog(recommendation)}
                >
                  {recommendation.photos && recommendation.photos.length > 0 && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={recommendation.photos[0]}
                      alt={recommendation.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip
                        icon={categoryDetails.icon}
                        label={categoryDetails.label}
                        size="small"
                        sx={{
                          bgcolor: categoryDetails.color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      {recommendation.createdBy === currentUser.uid && (
                        <Box>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(recommendation); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(recommendation.id, recommendation.photos); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: categoryDetails.color }}>
                      {recommendation.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={recommendation.rating || 0} readOnly size="small" />
                        <Typography variant="caption" color="text.secondary">
                          ({recommendation.rating || 0}/5)
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {recommendation.priceRange}
                      </Typography>
                    </Box>

                    {recommendation.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {recommendation.description}
                      </Typography>
                    )}

                    {(recommendation.location || recommendation.address) && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {recommendation.location?.name || recommendation.location?.address || recommendation.address}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, ml: 2.5, flexWrap: 'wrap' }}>
                          {buildMapsUrl(recommendation.location || { address: recommendation.address }) && (
                            <Button size="small" variant="text" sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                              onClick={(e) => { e.stopPropagation(); window.open(buildMapsUrl(recommendation.location || { address: recommendation.address }), '_blank'); }}>
                              Map
                            </Button>
                          )}
                          {buildDirectionsUrl(recommendation.location || { address: recommendation.address }) && (
                            <Button size="small" variant="text" startIcon={<DirectionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                              sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                              onClick={(e) => { e.stopPropagation(); window.open(buildDirectionsUrl(recommendation.location || { address: recommendation.address }), '_blank'); }}>
                              Directions
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}

                    {recommendation.tags && recommendation.tags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {recommendation.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}

                    {/* Social Contact Info */}
                    {(recommendation.contactPhone || recommendation.contactWebsite || recommendation.contactInstagram || recommendation.contactFacebook || recommendation.contactLinkedIn || recommendation.contactTwitter) && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {recommendation.contactPhone && (
                          <Chip icon={<PhoneIcon />} label={recommendation.contactPhone} size="small" variant="outlined" />
                        )}
                        {recommendation.contactWebsite && (
                          <Chip icon={<WebsiteIcon />} label={recommendation.contactWebsite} size="small" variant="outlined" clickable component="a" href={recommendation.contactWebsite.startsWith('http') ? recommendation.contactWebsite : `https://${recommendation.contactWebsite}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} />
                        )}
                        {recommendation.contactInstagram && (
                          <Chip icon={<InstagramIcon />} label={recommendation.contactInstagram} size="small" variant="outlined" />
                        )}
                        {recommendation.contactFacebook && (
                          <Chip icon={<FacebookIcon />} label={recommendation.contactFacebook} size="small" variant="outlined" />
                        )}
                        {recommendation.contactLinkedIn && (
                          <Chip icon={<LinkedInIcon />} label={recommendation.contactLinkedIn} size="small" variant="outlined" clickable component="a" href={recommendation.contactLinkedIn.startsWith('http') ? recommendation.contactLinkedIn : `https://${recommendation.contactLinkedIn}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} />
                        )}
                        {recommendation.contactTwitter && (
                          <Chip label={`X: ${recommendation.contactTwitter}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Button
                        size="small"
                        variant={userLiked ? 'contained' : 'outlined'}
                        color={userLiked ? 'error' : 'primary'}
                        startIcon={<FavoriteIcon />}
                        onClick={(e) => { e.stopPropagation(); handleLike(recommendation.id); }}
                      >
                        {likeCount}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<CommentIcon />}
                        onClick={(e) => { e.stopPropagation(); openCommentDialog(recommendation); }}
                      >
                        {commentCount}
                      </Button>
                      <Button
                        size="small"
                        variant={userVisited ? 'contained' : 'outlined'}
                        color={userVisited ? 'success' : 'primary'}
                        startIcon={<CheckCircleIcon />}
                        onClick={(e) => { e.stopPropagation(); openVisitedDialog(recommendation); }}
                      >
                        {visitedCount}
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {recommendation.createdByName}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Recommendation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecommendation ? 'Edit Recommendation' : 'Add Recommendation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Place/Activity Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Mario's Italian Restaurant, Central Park"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Tell your family about this place..."
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon}
                      <Typography>{cat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocationPicker
              value={formData.location}
              onChange={(loc) => setFormData({ ...formData, location: loc })}
              label="Location"
              placeholder="Search for a place or use GPS"
            />

            <Box>
              <Typography variant="caption" gutterBottom>
                Your Rating ({formData.rating}/5)
              </Typography>
              <Rating
                value={formData.rating}
                onChange={(event, newValue) => setFormData({ ...formData, rating: newValue || 0 })}
                size="large"
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Price Range</InputLabel>
              <Select
                value={formData.priceRange}
                label="Price Range"
                onChange={(e) => setFormData({ ...formData, priceRange: e.target.value })}
              >
                {priceRanges.map((price) => (
                  <MenuItem key={price} value={price}>{price}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="family-friendly, outdoor seating, vegetarian options"
            />

            {/* Photo Upload */}
            <Box>
              <Typography variant="caption" gutterBottom display="block">
                Photos (up to 5)
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                multiple
                type="file"
                onChange={handlePhotoUpload}
                disabled={formData.photos.length >= 5 || photoUploading}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={photoUploading ? <CircularProgress size={20} /> : <PhotoIcon />}
                  disabled={formData.photos.length >= 5 || photoUploading}
                >
                  {photoUploading ? 'Uploading...' : 'Add Photos'}
                </Button>
              </label>

              {formData.photos.length > 0 && (
                <ImageList sx={{ mt: 2 }} cols={3} rowHeight={100}>
                  {formData.photos.map((photo, index) => {
                    const safePhotoUrl = sanitizeImageUrl(photo);
                    if (!safePhotoUrl) return null;
                    return (
                      <ImageListItem key={index}>
                        <img
                          src={safePhotoUrl}
                          alt={`${index + 1}`}
                          loading="lazy"
                          style={{ height: '100px', objectFit: 'cover' }}
                        />
                        <IconButton
                          sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'white' }}
                          size="small"
                          onClick={() => handleRemovePhoto(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              )}
            </Box>

            <Divider sx={{ my: 2 }}>
              <Chip label="Social & Contact Info" size="small" />
            </Divider>

            {/* Social Contact Info */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder="(555) 123-4567"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Website"
                  value={formData.contactWebsite}
                  onChange={(e) => setFormData({ ...formData, contactWebsite: e.target.value })}
                  placeholder="https://example.com"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><WebsiteIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram"
                  value={formData.contactInstagram}
                  onChange={(e) => setFormData({ ...formData, contactInstagram: e.target.value })}
                  placeholder="@username"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Facebook"
                  value={formData.contactFacebook}
                  onChange={(e) => setFormData({ ...formData, contactFacebook: e.target.value })}
                  placeholder="facebook.com/page"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><FacebookIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="LinkedIn"
                  value={formData.contactLinkedIn}
                  onChange={(e) => setFormData({ ...formData, contactLinkedIn: e.target.value })}
                  placeholder="linkedin.com/in/profile"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkedInIcon fontSize="small" /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Twitter / X"
                  value={formData.contactTwitter}
                  onChange={(e) => setFormData({ ...formData, contactTwitter: e.target.value })}
                  placeholder="@handle"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            {/* Privacy Control */}
            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this recommendation?"
            />

            {/* Tag Members */}
            <TagMembers
              familyCode={familyBoardId}
              value={formData.taggedMembers}
              onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
              label="Tag Family Members"
              helperText="Tagged members will receive a notification about this recommendation"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingRecommendation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comments</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedRecommendation?.comments && selectedRecommendation.comments.length > 0 ? (
              selectedRecommendation.comments.map((comment, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {comment.userName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {comment.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(parseISO(comment.timestamp), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">{comment.comment}</Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to comment!
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
              />
              <Button variant="contained" onClick={handleAddComment}>
                Post
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Visited Dialog */}
      <Dialog open={visitedDialogOpen} onClose={() => setVisitedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Visited</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedRecommendation?.visited && Object.keys(selectedRecommendation.visited).length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Family Members Who Visited:
                </Typography>
                {Object.values(selectedRecommendation.visited).map((visit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 24, height: 24 }}>
                      {visit.name.charAt(0)}
                    </Avatar>
                    <Typography variant="caption">
                      {visit.name} - {format(parseISO(visit.date), 'MMM d, yyyy')}
                    </Typography>
                    <Rating value={visit.rating} readOnly size="small" />
                  </Box>
                ))}
              </Paper>
            )}

            <TextField
              fullWidth
              label="Visit Date"
              type="date"
              value={visitedData.date}
              onChange={(e) => setVisitedData({ ...visitedData, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="caption" gutterBottom>
                Your Rating ({visitedData.rating}/5)
              </Typography>
              <Rating
                value={visitedData.rating}
                onChange={(event, newValue) => setVisitedData({ ...visitedData, rating: newValue || 0 })}
                size="large"
              />
            </Box>

            <TextField
              fullWidth
              label="Notes (optional)"
              value={visitedData.notes}
              onChange={(e) => setVisitedData({ ...visitedData, notes: e.target.value })}
              multiline
              rows={3}
              placeholder="How was your experience?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitedDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleMarkVisited} variant="contained" color="success">
            Mark Visited
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail View Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
              {selectedRecommendation?.name}
            </Typography>
            {selectedRecommendation && selectedRecommendation.createdBy === currentUser.uid && (
              <Box>
                <IconButton size="small" onClick={() => { setDetailDialogOpen(false); openEditDialog(selectedRecommendation); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => { setDetailDialogOpen(false); handleDelete(selectedRecommendation.id, selectedRecommendation.photos); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRecommendation && (
            <Box sx={{ pt: 2 }}>
              {/* Photos Gallery */}
              {selectedRecommendation.photos && selectedRecommendation.photos.length > 0 && (
                <ImageList sx={{ mb: 3 }} cols={selectedRecommendation.photos.length === 1 ? 1 : 2} rowHeight={200}>
                  {selectedRecommendation.photos.map((photo, index) => {
                    const safePhotoUrl = sanitizeImageUrl(photo);
                    if (!safePhotoUrl) return null;
                    return (
                      <ImageListItem key={index}>
                        <img
                          src={safePhotoUrl}
                          alt={`${selectedRecommendation.name} ${index + 1}`}
                          loading="lazy"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              )}

              {/* Category and Rating */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Chip
                  icon={getCategoryDetails(selectedRecommendation.category).icon}
                  label={getCategoryDetails(selectedRecommendation.category).label}
                  sx={{
                    bgcolor: getCategoryDetails(selectedRecommendation.category).color,
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={selectedRecommendation.rating || 0} readOnly />
                  <Typography variant="body2" color="text.secondary">
                    ({selectedRecommendation.rating}/5)
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {selectedRecommendation.priceRange}
                </Typography>
              </Box>

              {/* Description */}
              {selectedRecommendation.description && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body1">{selectedRecommendation.description}</Typography>
                </Paper>
              )}

              {/* Location */}
              {(selectedRecommendation.location || selectedRecommendation.address) && (() => {
                // Merge location object + top-level address so buttons always show
                // even when location is a truthy-but-empty object {}
                const loc = {
                  name: selectedRecommendation.location?.name || null,
                  address: selectedRecommendation.location?.address || selectedRecommendation.address || null,
                  lat: selectedRecommendation.location?.lat || null,
                  lng: selectedRecommendation.location?.lng || null,
                };
                const displayName = loc.name || loc.address;
                const mapsUrl = buildMapsUrl(loc);
                const directionsUrl = buildDirectionsUrl(loc);
                return displayName ? (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
                      <Typography variant="body1">{displayName}</Typography>
                    </Box>
                    {(mapsUrl || directionsUrl) && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 4 }}>
                        {mapsUrl && (
                          <Button size="small" variant="outlined" startIcon={<LocationIcon />}
                            onClick={() => window.open(mapsUrl, '_blank')}>
                            View on Maps
                          </Button>
                        )}
                        {directionsUrl && (
                          <Button size="small" variant="outlined" startIcon={<DirectionsIcon />}
                            onClick={() => window.open(directionsUrl, '_blank')}>
                            Directions
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : null;
              })()}

              {/* Tags */}
              {selectedRecommendation.tags && selectedRecommendation.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {selectedRecommendation.tags.map((tag, index) => (
                    <Chip key={index} label={tag} variant="outlined" />
                  ))}
                </Box>
              )}

              {/* Social Contact Info */}
              {(selectedRecommendation.contactPhone || selectedRecommendation.contactWebsite || selectedRecommendation.contactInstagram || selectedRecommendation.contactFacebook || selectedRecommendation.contactLinkedIn || selectedRecommendation.contactTwitter) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Contact Info</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedRecommendation.contactPhone && (
                      <Chip icon={<PhoneIcon />} label={selectedRecommendation.contactPhone} variant="outlined" />
                    )}
                    {selectedRecommendation.contactWebsite && (
                      <Chip icon={<WebsiteIcon />} label={selectedRecommendation.contactWebsite} variant="outlined" clickable component="a" href={selectedRecommendation.contactWebsite.startsWith('http') ? selectedRecommendation.contactWebsite : `https://${selectedRecommendation.contactWebsite}`} target="_blank" rel="noopener noreferrer" />
                    )}
                    {selectedRecommendation.contactInstagram && (
                      <Chip icon={<InstagramIcon />} label={selectedRecommendation.contactInstagram} variant="outlined" />
                    )}
                    {selectedRecommendation.contactFacebook && (
                      <Chip icon={<FacebookIcon />} label={selectedRecommendation.contactFacebook} variant="outlined" />
                    )}
                    {selectedRecommendation.contactLinkedIn && (
                      <Chip icon={<LinkedInIcon />} label={selectedRecommendation.contactLinkedIn} variant="outlined" clickable component="a" href={selectedRecommendation.contactLinkedIn.startsWith('http') ? selectedRecommendation.contactLinkedIn : `https://${selectedRecommendation.contactLinkedIn}`} target="_blank" rel="noopener noreferrer" />
                    )}
                    {selectedRecommendation.contactTwitter && (
                      <Chip label={`X: ${selectedRecommendation.contactTwitter}`} variant="outlined" />
                    )}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3, justifyContent: 'center' }}>
                <Button
                  variant={hasUserLiked(selectedRecommendation.likes) ? 'contained' : 'outlined'}
                  color={hasUserLiked(selectedRecommendation.likes) ? 'error' : 'primary'}
                  startIcon={<FavoriteIcon />}
                  onClick={(e) => { e.stopPropagation(); handleLike(selectedRecommendation.id); }}
                >
                  {getLikeCount(selectedRecommendation.likes)} Likes
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CommentIcon />}
                  onClick={(e) => { e.stopPropagation(); setDetailDialogOpen(false); openCommentDialog(selectedRecommendation); }}
                >
                  {getCommentCount(selectedRecommendation.comments)} Comments
                </Button>
                <Button
                  variant={hasUserVisited(selectedRecommendation.visited) ? 'contained' : 'outlined'}
                  color={hasUserVisited(selectedRecommendation.visited) ? 'success' : 'primary'}
                  startIcon={<CheckCircleIcon />}
                  onClick={(e) => { e.stopPropagation(); setDetailDialogOpen(false); openVisitedDialog(selectedRecommendation); }}
                >
                  {getVisitedCount(selectedRecommendation.visited)} Visited
                </Button>
              </Box>

              {/* Visited History */}
              {selectedRecommendation.visited && Object.keys(selectedRecommendation.visited).length > 0 && (
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Family Visit History
                  </Typography>
                  {Object.values(selectedRecommendation.visited).map((visit, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {visit.name.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600}>
                          {visit.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(parseISO(visit.date), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                      <Rating value={visit.rating} readOnly size="small" />
                      {visit.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          "{visit.notes}"
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Paper>
              )}

              {/* Creator Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Recommended by
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedRecommendation.createdByName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Recommendations;
